import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { CreateContactField } from "../contacts/create-contact-field";
import ContactCheckboxList from "../content/contact-checkbox-list";
import { getAllContacts, getContactAffiliations } from "../../services/contact-service";
import { AreYouSureModal } from "../shared/are-you-sure";
import { useStandardQuery } from "../hooks/useStandardQuery";
import toast from "react-hot-toast";
import Select from "react-select";
import CustomOption from "../shared/react-select-option-check";
import CustomValueContainer from "../shared/react-select-custom-value-container";
import Loader from "../navigation/Loader";
import { getContent } from "../../services/content-service";
import { createOrUpdateEvent, deleteEventById } from "../../services/event-service";

export default function EventCreateEdit({ event = null }) {
  const [name, setName] = useState(event?.name || "");
  const [description, setDescription] = useState(event?.description || "");
  const [eventDate, setEventDate] = useState(event?.date || "");
  const [tags, setTags] = useState(event?.tags || []);
  const [inputTag, setInputTag] = useState("");
  const [allowedAffiliations, setAllowedAffiliations] = useState(event?.allowedAffiliations || []);
  const [contactsForEmail, setContactsForEmail] = useState([]);
  const [fullContactList, setFullContactList] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const affiliations = useStandardQuery(getContactAffiliations, null);
  const [content, setContent] = useState([]);
  const contactsList = useStandardQuery(getAllContacts, null);
  const { getAccessTokenSilently } = useAuth0();
  const [selectedContent, setSelectedContent] = useState(null);
  const editMode = event != null;
  const id = event?.id;

  useEffect(() => {
    if (contactsList.status === "success") {
      const newContacts = contactsList.data.map(c => ({ ...c, checked: false }));
      setFullContactList(newContacts);

      const filtered = allowedAffiliations.length
        ? newContacts.filter(c => allowedAffiliations.some(a => c.affiliation?.id === a.id))
        : newContacts;

      setContactsForEmail(filtered.map(c => ({ ...c, checked: false })));
    }
  }, [contactsList.status]);

  useEffect(() => {
    async function fetchContent() {
      const accessToken = await getAccessTokenSilently();
      const payload = {};
      const contentReturned = await getContent(accessToken, payload);
      const list = contentReturned.data.content || [];
      setContent(list);
      console.table(list.map(c => ({ id: String(c.id), desc: c.description })));
    }
    fetchContent();
  }, []);

  const contentOptions = useMemo(() => {
    return (content || []).map(c => ({
      value: String(c.id),
      label: c.description
    }));
  }, [content]);

  useEffect(() => {
    if (!event?.linkedContentId || contentOptions.length === 0) return;
    const targetId = String(event.linkedContentId).toLowerCase();
    const match = contentOptions.find(o => String(o.value).toLowerCase() === targetId);
    setSelectedContent(match ?? null);
  }, [event?.linkedContentId, contentOptions]);

  const handleAddTag = () => {
    if (inputTag.trim()) {
      setTags([...tags, inputTag.trim()]);
      setInputTag("");
    }
  };

  const handleRemoveTag = (index) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    setTags(newTags);
  };

  const handleAffiliationChange = (newAffiliations) => {
    const selected = newAffiliations.map(a => ({ id: a.value, name: a.label }));
    setAllowedAffiliations(selected);

    const filteredContacts = selected.length === 0
      ? fullContactList
      : fullContactList.filter(c => selected.some(a => c.affiliation?.id === a.id));

    setContactsForEmail(filteredContacts.map(c => ({ ...c, checked: false })));
  };

  const handleNotificationChange = () => {
    const nextVisible = !isVisible;
    setIsVisible(nextVisible);
    setContactsForEmail(contactsForEmail.map(c => ({ ...c, checked: nextVisible })));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const accessToken = await getAccessTokenSilently();
    const selectedPhoneList = contactsForEmail
      .filter(c => c.checked)
      .map(c => c.phone)
      .filter(Boolean);
    const selectedEmailList = contactsForEmail
      .filter(c => c.checked)
      .map(c => c.email)
      .filter(Boolean);
    const formData = new FormData();
    formData.append("Name", name);
    formData.append("Description", description);
    formData.append("Date", eventDate);
    formData.append("Tags", JSON.stringify(tags));
    formData.append("PhoneList", selectedPhoneList.join(","));
    formData.append("emailList", JSON.stringify(selectedEmailList));
    formData.append("AllowedAffiliations", JSON.stringify(allowedAffiliations));
    formData.append("LinkedContentID", selectedContent?.value || "");

    const result = await createOrUpdateEvent(accessToken, formData, id);

    if (!result.error) {
      toast.success(`Event ${id ? "updated" : "created"}!`);
      if (id) {
        window.location = `/Events/${id}`;
      }
    } else {
      toast.error(result.error.message || "There was an issue!");
    }

    setSubmitting(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const accessToken = await getAccessTokenSilently();
    const result = await deleteEventById(accessToken, id);

    if (!result.error) {
      toast.success("Event deleted!");
      window.location = '/Events/All';
    } else {
      toast.error(`Deletion failed: ${result.error.message}`);
    }

    setDeleting(false);
  };

  return (
    <div className="admin-panel-outer">
      <div className="admin-panel-header">{editMode ? "Edit" : "Create"} Event</div>
      <input
        className="content-upload-textinput"
        type="text"
        placeholder="Event name"
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <textarea
        className="content-upload-textinput"
        placeholder="Event description"
        value={description}
        onChange={e => setDescription(e.target.value)}
      />
      <input
        className="content-upload-textinput"
        type="datetime-local"
        value={eventDate}
        onChange={e => setEventDate(e.target.value)}
      />

      <div className="content-upload-tags">
        <input
          className="content-upload-textinput"
          type="text"
          placeholder="add a search tag"
          value={inputTag}
          onChange={e => setInputTag(e.target.value)}
          onKeyUp={e => e.key === "Enter" && handleAddTag()}
        />
        <button className="btn btn2" onClick={handleAddTag}>
          Add tag <i className="fa-regular fa-plus"></i>
        </button>
        <div className="content-tags-runninglist">
          {tags.map((tag, i) => (
            <div key={i} className="searchTagToAdd tag-temp">
              {tag}
              <i onClick={() => handleRemoveTag(i)} className="fa-regular fa-xmark"></i>
            </div>
          ))}
        </div>
      </div>

      <div className="content-upload-label" style={{ color: "white", marginTop: "10px" }}>
        Restrict Access
      </div>
      <Select
        options={affiliations?.data?.map(a => ({ value: a.id, label: a.name }))}
        placeholder="Select affiliations"
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ Option: CustomOption, ValueContainer: CustomValueContainer }}
        value={allowedAffiliations.map(a => ({ value: a.id, label: a.name }))}
        onChange={handleAffiliationChange}
        theme={theme => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...theme.colors,
            primary25: 'gray',
            primary: 'gray',
            neutral0: "#222222",
            neutral10: "white",
            neutral20: "gray"
          },
        })}
        styles={{
          container: (provided) => ({ ...provided, maxWidth: "400px", width: "100%" }),
          singleValue: (baseStyles) => ({ ...baseStyles, fontSize: "15px", color: "white" }),
        }}
      />

      <div className="content-upload-label" style={{ color: "white", marginTop: "10px" }}>
        Choose Content
      </div>
      <Select
        options={contentOptions}
        placeholder="Select content"
        value={selectedContent}
        onChange={setSelectedContent}
        isLoading={contentOptions.length === 0}
        theme={theme => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...theme.colors,
            primary25: 'gray',
            primary: 'gray',
            neutral0: "#222222",
            neutral10: "white",
            neutral20: "gray"
          },
        })}
        styles={{
          container: (provided) => ({ ...provided, maxWidth: "400px", width: "100%" }),
          singleValue: (baseStyles) => ({ ...baseStyles, fontSize: "15px", color: "white" }),
        }}
      />

      <CreateContactField
        type="checkbox"
        id="sendNotificationEventField"
        handleValueChange={handleNotificationChange}
        propName="sendNotification"
        label="Notify Members"
        style={{ justifyContent: "space-between", maxWidth: "unset" }}
      />

      {isVisible &&
        <ContactCheckboxList
          contacts={contactsForEmail}
          loading={contactsList.status === "loading"}
          handleChangeCheckBox={(e) => {
            const contactId = e.target.dataset.contactid;
            setContactsForEmail(prev =>
              prev.map(c => c.id === contactId ? { ...c, checked: !c.checked } : c)
            );
          }}
          handleCheckAllChange={(allChecked) => {
            setContactsForEmail(prev => prev.map(c => ({ ...c, checked: allChecked })));
          }}
        />
      }

      <div className="contact-update-delete-row" style={{ width: "100%", marginTop: "12px" }}>
        {editMode &&
          <button className="btn btnDelete" onClick={() => setShowModal(true)}>
            <i className="fa-solid fa-trash"></i><span>Delete</span>
          </button>
        }
        <button className="btn btn1" onClick={handleSubmit}>
          {isSubmitting ? <Loader /> : <><span>Save</span><i className="fa-regular fa-arrow-right"></i></>}
        </button>
      </div>

      <AreYouSureModal
        onCompleteAction={handleDelete}
        handleCancelAction={() => setShowModal(false)}
        loadingState={isDeleting}
        isVisible={showModal}
      />
    </div>
  );
}